import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./GettingStarted.module.css";

// Components
import ShowAndHide from "../../../common-ui-components/components/show-and-hide/ShowAndHide";
import BulkDeployment from "../../enrollment-new/devices/deployment/BulkDeployment";
import ManualDeployment from "../../enrollment-new/devices/deployment/ManualDeployment";
import ConfigureScheduler from "../../inventory-new/schedule-scan/ConfigureScheduler";
import ViewSchedulers from "../../inventory-new/schedule-scan/ViewSchedulers";
import Modal from "../../../common-ui-components/components/modal";
import Button from "../../../common-ui-components/components/button/Button";
import Message from "../../../common-ui-components/components/messager/Message";
import CustomSelect from "../../settings/organization/UI/CustomSelect";
import ResponseLoader from "../../../common-ui-components/components/ResponseLoader/ResponseLoader";

// Utilities & Context
import EM_API from "../../../common-ui-components/components/utils/API";
import { notify } from "../../../common-ui-components/components/notification/Notification";
import { Failure } from "../../../common-ui-components/components/notification/templates";
import { UserDetailsContext } from "App";

// Icons
import Delete from "../../../common-ui-components/components/icons/delete.svg";
import tick from "./tick.svg";
import { PRODUCT_CONTEXT } from "common-ui-components/components/utils/Config";

function GettingStarted() {
  const navigate = useNavigate();
  const userDetails = useContext(UserDetailsContext);

  // --- STATE ---
  const [showSetup, setShowSetup] = useState(true);
  const [showComplete, setShowComplete] = useState(false);
  const [forceShowSetup, setForceShowSetup] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userForms, setUserForms] = useState([
    { email: "", username: "", role: "" },
  ]);
  const [isSetupCompleted, setIsSetupCompleted] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Steps
  const [step1, setStep1] = useState(null);
  const [step2, setStep2] = useState(null);
  const [step3, setStep3] = useState(null);

  // Constants
  const MAX_FORMS = 5;
  let productDescription;
  let videoId;
  switch (PRODUCT_CONTEXT) {
    case "em":
      videoId = "fvMOlxMwjUk";
      productDescription =
        "Discover the key features of Endpoint Management software, such as Inventory, Patch Management, Remote Access, Reporting, and Device Configurations. ";
      break;
    case "am":
      videoId = "_d5bqT0WFUI";
      productDescription =
        "Discover the key features of ITAM software, such as Device Enrollment, Inventory, Reporting, and Settings.";
      break;
    case "ra":
      //this is the wrong one but form now the AM one is added
      videoId = "_d5bqT0WFUI";
      break;
    default:
      videoId = "fvMOlxMwjUk";
      break;
  }
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const iframeRef = useRef(null);

  // Permission Check
  const userAction = userDetails?.userAction || [];
  const isRestricted = userAction.includes("UM_VIEW_USER");

  // --- OPTIMIZED DATA FETCHING (Only runs once) ---
  useEffect(() => {
    if (isRestricted) return;

    let isMounted = true;

    const fetchStartupData = async () => {
      setLoading(true);
      try {
        const [rolesResponse, stepsResponse] = await Promise.all([
          EM_API.get("/roles/viewCompressedRoles"),
          EM_API.get("/UM/quick-setup/steps"),
        ]);

        if (isMounted) {
          // Process Roles
          const rolesArray = rolesResponse || [];
          const formattedRoles = rolesArray
            .filter(
              (role) => !(role.roleId === 1 && role.roleName === "Super Admin"),
            )
            .map((role) => ({
              label: role.roleName,
              value: role.roleId,
            }));
          setRoles(formattedRoles);

          // Process Steps
          if (stepsResponse && stepsResponse.body) {
            setShowComplete(stepsResponse.body.quickSetupEnabled);
            const steps = stepsResponse.body.steps || [];
            setStep1(steps[0] || false);
            setStep2(steps[1] || false);
            setStep3(steps[2] || false);
          }
        }
      } catch (error) {
        console.error("Error fetching startup data:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStartupData();

    return () => {
      isMounted = false;
    };
  }, [isRestricted]);

  // Video Modal Cleanup
  useEffect(() => {
    if (!isOpen && iframeRef.current) {
      const playerCommand = JSON.stringify({
        event: "command",
        func: "pauseVideo",
      });
      iframeRef.current.contentWindow.postMessage(playerCommand, "*");
    }
  }, [isOpen]);

  if (isRestricted) return null;

  // Progress Logic
  let count = 0;
  if (step1) count += 1;
  if (step2) count += 1;
  if (step3) count += 1;

  const shouldShowSetup = !showComplete || forceShowSetup || count < 3;

  // --- STRICTLY FIXED REVERT FUNCTION ---
  const revertComplete = async (e) => {
    // 1. STOP BROWSER DEFAULT BEHAVIOR (Prevents Form Submit/Reload)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // 2. OPTIMISTIC UI UPDATE (Immediate)
    // Do NOT call setLoading(true) here, it causes the "white screen" flash.
    setForceShowSetup(true);
    setShowComplete(false);

    // 3. SILENT API CALL
    try {
      await EM_API.get("/UM/update/quick-setup?quickSetupEnabled=false");
    } catch (error) {
      // Revert state only if API fails
      setForceShowSetup(false);
      setShowComplete(true);
      notify(<Failure>Failed to revert setup. Please try again.</Failure>);
      console.error("Error reverting setup:", error);
    }
  };

  const markAsCompleted = async () => {
    setLoading(true);
    try {
      await EM_API.get("/UM/update/quick-setup?quickSetupEnabled=true");
      setShowConfirmModal(false);
      setIsSetupCompleted(true);
      window.location.reload(); // Explicit reload for completion
    } catch (error) {
      notify(<Failure>Failed to mark as complete. Please try again.</Failure>);
      console.error("Error marking setup:", error);
    } finally {
      setLoading(false);
    }
  };

  const validatePayLoad = (payload) => {
    const errors = [];

    payload.forEach((user, index) => {
      if (!user.email) errors.push(`email is required\n`);
      if (!user.username) errors.push(`username is required\n`);
      if (!user.roleId) errors.push(`role is required\n`);
    });

    return errors;
  };

  const inviteUser = async () => {
    setLoading(true);
    try {
      const payload = userForms.map((user) => ({
        email: user.email,
        username: user.username,
        roleId: user.role,
        deviceAccess: 0,
        productId: 1,
        group: [-1],
      }));

      const errors = validatePayLoad(payload);
      if (errors.length > 0) {
        notify(<Failure>{errors.join(", ")}</Failure>);
        setLoading(false);
        return;
      }

      const response = await EM_API.post("UM/quick-invite", payload);
      if (response.status === 200) {
        notify("Invitations sent successfully!");
        setIsSetupCompleted(response.data.isSetupCompleted);
      } else {
        notify(<Failure>{response.message}</Failure>);
      }
    } catch (error) {
      notify(<Failure>{error.message}</Failure>);
    } finally {
      setLoading(false);
    }
  };
  // Form Handlers
  const handleInputChange = (index, field, value) => {
    const updatedForms = [...userForms];
    updatedForms[index][field] = value;
    setUserForms(updatedForms);
  };

  const addNewForm = () => {
    if (userForms.length < MAX_FORMS)
      setUserForms([...userForms, { email: "", username: "", role: "" }]);
    else notify(<Failure>The home page restricts only 5 users</Failure>);
  };

  const deleteForm = (index) => {
    if (userForms.length > 1)
      setUserForms(userForms.filter((_, i) => i !== index));
  };

  const handleNavigate = () => {
    navigate("/app/settings/organization/users");
  };

  if (loading || step3 === null || step2 === null || step1 === null)
    return <ResponseLoader rootLevel />;

  return (
    <div className={styles.main}>
      <h1 className={styles.welcome}>Welcome to Zecurit</h1>
      <p className={styles.para}>
        Effortlessly manage your IT assets from a single platform, starting now.
      </p>

      <div className={styles.video}>
        <div
          className={styles.thumbnailContainer}
          onClick={() => setIsOpen(true)}
        >
          <img
            width="160px"
            src={thumbnailUrl}
            alt="Video Thumbnail"
            className={styles.thumbnail}
          />
          <div className={styles.playButton}>▶</div>
        </div>
        <Modal open={isOpen} setOpen={setIsOpen}>
          <div style={{ margin: "25px" }}>
            <iframe
              ref={iframeRef}
              style={{ border: "none" }}
              width="900"
              height="600"
              src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1`}
              title="YouTube Video"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </Modal>
        <div className={styles.vidDescription}>
          <p className={styles.vidtitle}>Getting Started with Zecurit</p>
          <p className={styles.para}>{productDescription}</p>
        </div>
      </div>

      <Message
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Completion"
        description="Are you sure you want to mark Quick Setup as completed?"
        confirmType="confirm"
        onConfirm={markAsCompleted}
        onCancel={() => setShowConfirmModal(false)}
      />

      {/* --- BRING BACK SETUP BUTTON --- */}
      {/* Added type="button" and robust event handling to prevent refresh */}
      {showComplete && showSetup && !shouldShowSetup && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: "18px",
          }}
        >
          <Button
            type="button"
            onClick={(e) => revertComplete(e)}
            children={"Bring back the Setup"}
            className={styles.btn}
          />
        </div>
      )}

      {shouldShowSetup && (
        <div className={styles.showandhide}>
          <div style={{ marginBottom: "-10px" }} className={styles.top}>
            <p style={{ paddingTop: "36px" }} className={styles.welcome}>
              Quick Setup ({count}/3)
            </p>

            {/* --- MARK AS COMPLETED BUTTON --- */}
            {count === 3 && (
              <Button
                type="button"
                onClick={() => setShowConfirmModal(true)}
                children={"Mark As Completed"}
                className={styles.btn}
              />
            )}
          </div>

          {/* Step 1 */}
          <ShowAndHide
            entire={true}
            headerText={
              <>
                Install your first agent{" "}
                {step1 && <img className={styles.tick} src={tick} alt="tick" />}
              </>
            }
            initiallyOpen={false}
            containerProps={{ style: { backgroundColor: "#FFFFFF" } }}
            headerProps={{
              style: {
                backgroundColor: "#FFFFFF",
                fontFamily: "Inter",
                fontWeight: "500",
              },
            }}
          >
            <div className={styles.content}>
              <ManualDeployment />
              <div className={styles.bulkDeployment}>
                <ShowAndHide headerText={"Bulk Deployment"}>
                  <BulkDeployment />
                </ShowAndHide>
              </div>
            </div>
          </ShowAndHide>

          {/* Step 2 */}
          <ShowAndHide
            entire={true}
            headerText={
              <>
                Invite Users{" "}
                {step2 && <img className={styles.tick} src={tick} alt="tick" />}
              </>
            }
            initiallyOpen={false}
            headerProps={{
              style: {
                backgroundColor: "white",
                fontFamily: "Inter",
                fontWeight: "500",
                padding: "10px",
              },
            }}
          >
            <div style={{ backgroundColor: "white" }}>
              <div className={styles.tech}>
                <label>Email Address</label>
                <label>User Name</label>
                <label>Roles</label>
              </div>
              {userForms.map((form, index) => (
                <div key={index} className={styles.formRow}>
                  <input
                    type="text"
                    autoComplete="off"
                    value={form.email}
                    onChange={(e) =>
                      handleInputChange(index, "email", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) =>
                      handleInputChange(index, "username", e.target.value)
                    }
                  />
                  <div className={styles.select}>
                    <CustomSelect
                      data={roles}
                      valueKey="value"
                      displayKey="label"
                      selected={form.role}
                      valueSetter={(val) =>
                        handleInputChange(index, "role", val)
                      }
                      placeholder="Select a Role"
                    />
                  </div>
                  <img
                    src={Delete}
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    alt="Delete"
                    onClick={() => deleteForm(index)}
                  />
                </div>
              ))}
              <button
                onClick={addNewForm}
                style={{
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
              >
                <p style={{ paddingLeft: "8px", color: "#2F7EE9" }}>
                  + Add new
                </p>
              </button>
              <div className={styles.inviteFotter}>
                <Button
                  onClick={inviteUser}
                  children={"Send Invite"}
                  variant={"primary"}
                />
                <Button
                  onClick={() =>
                    setUserForms([{ email: "", username: "", role: "" }])
                  }
                  children={"Cancel"}
                  variant={"secondary"}
                />
                <div className={styles.info}>
                  <p style={{ fontWeight: "500" }}>
                    Navigate to{" "}
                    <span
                      onClick={handleNavigate}
                      style={{ color: "#2F7EE9", cursor: "pointer" }}
                    >
                      Users Page
                    </span>{" "}
                    to manage users.
                  </p>
                </div>
              </div>
            </div>
          </ShowAndHide>

          {/* Step 3 */}
          <ShowAndHide
            entire={true}
            headerText={
              <>
                Scan Assets or Schedule Asset Scanning{" "}
                {step3 && <img className={styles.tick} src={tick} alt="tick" />}
              </>
            }
            initiallyOpen={false}
            containerProps={{ style: { backgroundColor: "#FFFFFF" } }}
            headerProps={{
              style: {
                backgroundColor: "white",
                fontFamily: "Inter",
                fontWeight: "500",
              },
            }}
          >
            <div style={{ marginTop: "-0.6rem" }}>
              {step3 ? (
                <ViewSchedulers home={true} />
              ) : (
                <ConfigureScheduler home={true} />
              )}
            </div>
          </ShowAndHide>
        </div>
      )}
    </div>
  );
}

export default GettingStarted;
